import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import toast from "react-hot-toast";
import {
  Search,
  MapPin,
  Users,
  Euro,
  Calendar,
  Filter,
  X,
  Sparkles,
  Clock,
  Heart,
  Star,
  Wifi,
  Car,
  Coffee,
  Camera,
  Music,
  Utensils,
  TreePine,
  Building,
  Home,
  Palette,
  ChevronDown,
  SlidersHorizontal,
  Zap,
  TrendingUp,
  Waves,
  Sun,
  Lightbulb,
  Package,
  Armchair,
  Flame,
  UtensilsCrossed,
} from "lucide-react";

const server = process.env.REACT_APP_SERVER_URL || "";

interface SmartFiltersProps {
  className?: string;
  onFiltersChange: (filters: any) => void;
  currentFilters?: any;
}

interface QuickFilter {
  id: string;
  label: string;
  icon: any;
  color: string;
  popular?: boolean;
  filters: any;
}

interface Amenity {
  id: string;
  label: string;
  icon: any;
  color: string;
}

// Fonction de mapping des désignations vers les icônes
const getIconForAmenity = (designation: string): any => {
  const lowerDesignation = designation.toLowerCase();

  // Mapping basé sur des mots-clés dans la désignation
  if (lowerDesignation.includes("wifi") || lowerDesignation.includes("internet")) return Wifi;
  if (lowerDesignation.includes("parking") || lowerDesignation.includes("garage")) return Car;
  if (lowerDesignation.includes("cuisine") || lowerDesignation.includes("kitchen")) return Coffee;
  if (lowerDesignation.includes("photo") || lowerDesignation.includes("camera")) return Camera;
  if (
    lowerDesignation.includes("sono") ||
    lowerDesignation.includes("music") ||
    lowerDesignation.includes("audio")
  )
    return Music;
  if (
    lowerDesignation.includes("traiteur") ||
    lowerDesignation.includes("catering") ||
    lowerDesignation.includes("restaurant")
  )
    return Utensils;
  if (
    lowerDesignation.includes("jardin") ||
    lowerDesignation.includes("garden") ||
    lowerDesignation.includes("vert")
  )
    return TreePine;
  if (
    lowerDesignation.includes("terrasse") ||
    lowerDesignation.includes("terrace") ||
    lowerDesignation.includes("balcon")
  )
    return Building;
  if (lowerDesignation.includes("piscine") || lowerDesignation.includes("pool")) return Waves;
  if (
    lowerDesignation.includes("climatisation") ||
    lowerDesignation.includes("air") ||
    lowerDesignation.includes("clim")
  )
    return Sun;
  if (
    lowerDesignation.includes("lumière") ||
    lowerDesignation.includes("éclairage") ||
    lowerDesignation.includes("light")
  )
    return Lightbulb;

  // Équipements (Equipment table)
  if (lowerDesignation.includes("table")) return UtensilsCrossed;
  if (lowerDesignation.includes("chaise") || lowerDesignation.includes("chair")) return Armchair;
  if (lowerDesignation.includes("hamac")) return TreePine;
  if (lowerDesignation.includes("parasol")) return Sun;
  if (lowerDesignation.includes("barbecue") || lowerDesignation.includes("bbq")) return Flame;
  if (lowerDesignation.includes("wc") || lowerDesignation.includes("toilette")) return Building;

  // Aire de jeux (PlayGround table)
  if (lowerDesignation.includes("pétanque")) return Package;
  if (lowerDesignation.includes("balançoire")) return Users;
  if (lowerDesignation.includes("ping-pong")) return Package;
  if (lowerDesignation.includes("sable")) return Package;
  if (lowerDesignation.includes("badminton")) return Package;

  // Icône par défaut
  return Package;
};

// Fonction de mapping des couleurs
const getColorForAmenity = (designation: string): string => {
  const lowerDesignation = designation.toLowerCase();

  if (lowerDesignation.includes("wifi") || lowerDesignation.includes("internet")) return "text-blue-500";
  if (lowerDesignation.includes("parking")) return "text-green-500";
  if (lowerDesignation.includes("cuisine")) return "text-orange-500";
  if (lowerDesignation.includes("photo")) return "text-purple-500";
  if (lowerDesignation.includes("sono") || lowerDesignation.includes("music")) return "text-red-500";
  if (lowerDesignation.includes("traiteur")) return "text-yellow-500";
  if (lowerDesignation.includes("jardin")) return "text-green-600";
  if (lowerDesignation.includes("terrasse")) return "text-gray-500";
  if (lowerDesignation.includes("piscine")) return "text-cyan-500";
  if (lowerDesignation.includes("climatisation")) return "text-sky-500";
  if (lowerDesignation.includes("lumière")) return "text-amber-400";

  // Équipements
  if (lowerDesignation.includes("table")) return "text-brown-500";
  if (lowerDesignation.includes("chaise")) return "text-brown-600";
  if (lowerDesignation.includes("hamac")) return "text-green-500";
  if (lowerDesignation.includes("parasol")) return "text-yellow-500";
  if (lowerDesignation.includes("barbecue")) return "text-red-600";
  if (lowerDesignation.includes("wc")) return "text-gray-500";

  // Aire de jeux
  if (lowerDesignation.includes("pétanque")) return "text-orange-500";
  if (lowerDesignation.includes("balançoire")) return "text-pink-500";
  if (lowerDesignation.includes("ping-pong")) return "text-blue-600";
  if (lowerDesignation.includes("sable")) return "text-yellow-600";
  if (lowerDesignation.includes("badminton")) return "text-green-500";

  return "text-gray-600";
};

const SmartFilters: React.FC<SmartFiltersProps> = ({ className = "", onFiltersChange, currentFilters = {} }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [activeFilters, setActiveFilters] = useState<any>(currentFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<string[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [capacity, setCapacity] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("");
  const [sortBy, setSortBy] = useState("popularity");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [isLoadingAmenities, setIsLoadingAmenities] = useState(true);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const locationContainerRef = useRef<HTMLDivElement>(null);

  // Google Places Autocomplete
  const { placesService, placePredictions, isPlacePredictionsLoading, getPlacePredictions } = usePlacesService({
    apiKey: process.env.REACT_APP_MAPS_API_KEY,
    options: {
      input: "",
      componentRestrictions: {
        country: "fr",
      },
      types: ["(cities)"], // Restrict to cities
    },
  });

  // Filtres rapides intelligents
  const quickFilters: QuickFilter[] = [
    {
      id: "available_weekend",
      label: "Dispo ce weekend",
      icon: Calendar,
      color: "from-green-500 to-emerald-500",
      popular: true,
      filters: { availableWeekend: true },
    },
    {
      id: "garden_pool",
      label: "Jardin avec piscine",
      icon: TreePine,
      color: "from-blue-500 to-cyan-500",
      popular: true,
      filters: { amenities: ["Jardin", "Piscine"] },
    },
    {
      id: "near_metro",
      label: "Proche métro",
      icon: MapPin,
      color: "from-purple-500 to-violet-500",
      filters: { nearMetro: true },
    },
    {
      id: "instant_book",
      label: "Réservation instant.",
      icon: Zap,
      color: "from-orange-500 to-red-500",
      filters: { instantBook: true },
    },
    {
      id: "trending",
      label: "Tendance",
      icon: TrendingUp,
      color: "from-pink-500 to-rose-500",
      popular: true,
      filters: { trending: true },
    },
    {
      id: "luxury",
      label: "Haut de gamme",
      icon: Star,
      color: "from-yellow-500 to-amber-500",
      filters: { category: "luxury" },
    },
  ];

  // Chargement des équipements depuis l'API (PlayGround + Equipment)
  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        setIsLoadingAmenities(true);

        // Charger PlayGround (aire de jeux) et Equipment (équipements) en parallèle
        const [playgroundsRes, equipmentsRes] = await Promise.all([
          fetch(`${server}/api/playground`),
          fetch(`${server}/api/equipments`),
        ]);

        if (!playgroundsRes.ok || !equipmentsRes.ok) {
          throw new Error("Erreur lors du chargement des équipements");
        }

        const playgrounds = await playgroundsRes.json();
        const equipments = await equipmentsRes.json();

        // Combiner PlayGround (aire de jeux) + Equipment (équipements)
        const amenitiesList: Amenity[] = [
          ...playgrounds.map((p: any) => ({
            id: p.designation,
            label: p.designation,
            icon: getIconForAmenity(p.designation),
            color: getColorForAmenity(p.designation),
          })),
          ...equipments.map((e: any) => ({
            id: e.designation,
            label: e.designation,
            icon: getIconForAmenity(e.designation),
            color: getColorForAmenity(e.designation),
          })),
        ];

        setAmenities(amenitiesList);
      } catch (error) {
        console.error("Erreur lors du chargement des équipements:", error);
        toast.error("Impossible de charger les équipements");
        // Fallback vers une liste vide en cas d'erreur
        setAmenities([]);
      } finally {
        setIsLoadingAmenities(false);
      }
    };

    fetchAmenities();
  }, []);

  // Options de tri
  const sortOptions = [
    { value: "popularity", label: "Popularité" },
    { value: "price_low", label: "Prix croissant" },
    { value: "price_high", label: "Prix décroissant" },
    { value: "rating", label: "Mieux notés" },
    { value: "newest", label: "Plus récents" },
    { value: "distance", label: "Distance" },
  ];

  // Ranges de capacité
  const capacityOptions = [
    { value: "", label: "Toutes capacités" },
    { value: "1-10", label: "1-10 personnes" },
    { value: "11-25", label: "11-25 personnes" },
    { value: "26-50", label: "26-50 personnes" },
    { value: "51-100", label: "51-100 personnes" },
    { value: "100+", label: "100+ personnes" },
  ];

  const handleQuickFilterToggle = (filterId: string) => {
    const newSelected = selectedQuickFilters.includes(filterId)
      ? selectedQuickFilters.filter((id) => id !== filterId)
      : [...selectedQuickFilters, filterId];

    setSelectedQuickFilters(newSelected);

    // Merge filters from selected quick filters
    const mergedFilters = { ...activeFilters };
    newSelected.forEach((id) => {
      const filter = quickFilters.find((f) => f.id === id);
      if (filter) {
        Object.assign(mergedFilters, filter.filters);
      }
    });

    setActiveFilters(mergedFilters);
    onFiltersChange(mergedFilters);
  };

  const handleAmenityToggle = (amenityId: string) => {
    const newSelected = selectedAmenities.includes(amenityId)
      ? selectedAmenities.filter((id) => id !== amenityId)
      : [...selectedAmenities, amenityId];

    setSelectedAmenities(newSelected);
    const newFilters = { ...activeFilters, amenities: newSelected };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handlePriceChange = (newRange: number[]) => {
    setPriceRange(newRange);
    const newFilters = { ...activeFilters, minPrice: newRange[0], maxPrice: newRange[1] };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleCapacityChange = (newCapacity: string) => {
    setCapacity(newCapacity);
    const newFilters = { ...activeFilters, capacity: newCapacity };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
    const newFilters = { ...activeFilters, sortBy: newSort };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Handle location input change for autocomplete
  const handleLocationInputChange = (value: string) => {
    setLocation(value);
    if (value.length > 2) {
      getPlacePredictions({ input: value });
      setShowLocationDropdown(true);
    } else {
      setShowLocationDropdown(false);
    }
  };

  // Handle location selection from autocomplete
  const handleLocationSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    setIsLoadingPlaces(true);

    if (!placesService) {
      setIsLoadingPlaces(false);
      return;
    }

    placesService.getDetails({ placeId: prediction.place_id }, (placeDetails: any, status: any) => {
      if (status !== "OK") {
        toast.error("Erreur lors de la sélection du lieu, veuillez réessayer!");
        setIsLoadingPlaces(false);
        return;
      }

      const parser = new DOMParser();
      const adr_address = parser.parseFromString(placeDetails?.adr_address ?? "", "text/html");
      const streetAddress = adr_address.querySelector(".street-address")?.textContent || "";

      let selectedLocation = "";
      if (streetAddress !== placeDetails?.name) {
        selectedLocation = placeDetails?.name;
      } else {
        selectedLocation = streetAddress;
      }

      setLocation(selectedLocation);
      setShowLocationDropdown(false);
      setIsLoadingPlaces(false);
      getPlacePredictions({ input: "" });
    });
  };

  // Initialize filters with currentFilters values
  useEffect(() => {
    if (currentFilters) {
      if (currentFilters.location && !location) {
        setLocation(currentFilters.location);
      }
      if (currentFilters.capacity && !capacity) {
        setCapacity(currentFilters.capacity);
      }
      if (currentFilters.search && !searchQuery) {
        setSearchQuery(currentFilters.search);
      }
    }
  }, [currentFilters, location, capacity, searchQuery]);

  // Handle click outside location dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationContainerRef.current && !locationContainerRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format prediction description (remove France)
  const formatPredictionDescription = (description: string) => {
    return description.replace(/,\s*France$/, "").trim();
  };

  const handleSearch = () => {
    const newFilters = {
      ...activeFilters,
      search: searchQuery,
      location: location,
    };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    setSelectedQuickFilters([]);
    setSelectedAmenities([]);
    setPriceRange([0, 5000]);
    setCapacity("");
    setSearchQuery("");
    setLocation("");
    setSortBy("popularity");
    setShowLocationDropdown(false);
    setActiveFilters({});
    onFiltersChange({});
    getPlacePredictions({ input: "" });
  };

  const activeFiltersCount =
    selectedQuickFilters.length +
    selectedAmenities.length +
    (capacity ? 1 : 0) +
    (searchQuery ? 1 : 0) +
    (location ? 1 : 0);

  return (
    <div
      className={`bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 overflow-hidden ${className}`}
      ref={ref}
    >
      {/* Header avec recherche principale */}
      <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {/* Barre de recherche principale */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un lieu (ex: loft industriel, château...)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1 relative" ref={locationContainerRef}>
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
              <input
                ref={locationInputRef}
                type="text"
                placeholder="Localisation (Paris, Lyon, Marseille...)"
                value={location}
                onChange={(e) => handleLocationInputChange(e.target.value)}
                onFocus={() => location.length > 2 && setShowLocationDropdown(true)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {/* Autocomplete Dropdown */}
              <AnimatePresence>
                {showLocationDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-30 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl max-h-60 overflow-y-auto"
                  >
                    {isPlacePredictionsLoading || isLoadingPlaces ? (
                      <div className="flex items-center justify-center py-4 space-x-2">
                        <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                        <div
                          className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"
                          style={{ animationDelay: "0.2s" }}
                        ></div>
                        <div
                          className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"
                          style={{ animationDelay: "0.4s" }}
                        ></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">Recherche...</span>
                      </div>
                    ) : placePredictions.length > 0 ? (
                      <ul className="py-2">
                        {placePredictions.map((prediction, index) => (
                          <motion.li
                            key={prediction.place_id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            onClick={() => handleLocationSelect(prediction)}
                            className="px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center space-x-3"
                          >
                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatPredictionDescription(prediction.description)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {prediction.structured_formatting?.secondary_text || "France"}
                              </div>
                            </div>
                          </motion.li>
                        ))}
                      </ul>
                    ) : location.length > 2 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                        Aucune localisation trouvée
                      </div>
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSearch}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2"
            >
              <Search className="w-5 h-5" />
              <span>Rechercher</span>
            </motion.button>
          </div>

          {/* Filtres rapides populaires */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">Filtres populaires:</span>
            </div>
            {quickFilters
              .filter((f) => f.popular)
              .map((filter) => {
                const Icon = filter.icon;
                const isSelected = selectedQuickFilters.includes(filter.id);
                return (
                  <motion.button
                    key={filter.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleQuickFilterToggle(filter.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 text-sm font-medium ${
                      isSelected
                        ? `bg-gradient-to-r ${filter.color} text-white shadow-lg`
                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{filter.label}</span>
                  </motion.button>
                );
              })}
          </div>
        </motion.div>
      </div>

      {/* Filtres avancés */}
      <div className="p-6">
        {/* Toggle filtres avancés */}
        <div className="flex items-center justify-between mb-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <SlidersHorizontal className="w-5 h-5" />
            <span className="font-medium">Filtres avancés</span>
            <ChevronDown
              className={`w-4 h-4 transform transition-transform ${showAdvanced ? "rotate-180" : ""}`}
            />
          </motion.button>

          <div className="flex items-center space-x-4">
            {activeFiltersCount > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={clearAllFilters}
                className="flex items-center space-x-1 text-red-500 hover:text-red-600 text-sm"
              >
                <X className="w-4 h-4" />
                <span>Effacer tout ({activeFiltersCount})</span>
              </motion.button>
            )}

            {/* Tri */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Trier par:</span>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {showAdvanced && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Tous les filtres rapides */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <Filter className="w-4 h-4 mr-2" />
                  Filtres rapides
                </h3>
                <div className="flex flex-wrap gap-2">
                  {quickFilters.map((filter) => {
                    const Icon = filter.icon;
                    const isSelected = selectedQuickFilters.includes(filter.id);
                    return (
                      <motion.button
                        key={filter.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleQuickFilterToggle(filter.id)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 text-xs ${
                          isSelected
                            ? `bg-gradient-to-r ${filter.color} text-white shadow-md`
                            : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                        }`}
                      >
                        <Icon className="w-3 h-3" />
                        <span>{filter.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Équipements et Aire de jeux */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <Home className="w-4 h-4 mr-2" />
                  Équipements et Aire de jeux
                </h3>
                {isLoadingAmenities ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                      <div
                        className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                      <div
                        className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"
                        style={{ animationDelay: "0.4s" }}
                      ></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                        Chargement des équipements...
                      </span>
                    </div>
                  </div>
                ) : amenities.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    Aucun équipement disponible
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {amenities.map((amenity) => {
                      const Icon = amenity.icon;
                      const isSelected = selectedAmenities.includes(amenity.id);
                      return (
                        <motion.button
                          key={amenity.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleAmenityToggle(amenity.id)}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 text-xs ${
                            isSelected
                              ? "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 border-2 border-blue-300"
                              : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                          }`}
                        >
                          <Icon className={`w-3 h-3 ${amenity.color}`} />
                          <span>{amenity.label}</span>
                        </motion.button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Capacité et Prix */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Capacité */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    Capacité d'accueil
                  </h3>
                  <select
                    value={capacity}
                    onChange={(e) => handleCapacityChange(e.target.value)}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {capacityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Prix */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                    <Euro className="w-4 h-4 mr-2" />
                    Budget par jour
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>{priceRange[0]}€</span>
                      <span>{priceRange[1]}€</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="50"
                      value={priceRange[1]}
                      onChange={(e) => handlePriceChange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 slider"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SmartFilters;
